import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Select,
  OutlinedInput,
  Checkbox,
  ListItemText,
  TextField,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  FormControl,
  InputLabel,
} from "@mui/material";
import { useUserData } from "components/customHooks/userDataContext";
import {
  ERROR_LOCATIONS,
  ERROR_REASON_OPTIONS,
  NO_ERROR_OPTIONS,
} from "./helper";

const parseErrorDetails = (value) => {
  const empty = {
    current_status: "",
    no_error_reason: "",
    additional_notes: "",
    yes_data: {
      error_locations: [],
      reasons_by_location: {},
      notes_by_location: {},
    },
    history: [],
  };

  if (!value) return empty;

  let parsed = value;

  if (typeof value === "string") {
    try {
      parsed = JSON.parse(value);
    } catch {
      return empty;
    }
  }

  return {
    current_status: parsed?.current_status || "",
    no_error_reason: parsed?.no_error_reason || "",
    additional_notes: parsed?.additional_notes || "",
    yes_data: {
      error_locations: Array.isArray(parsed?.yes_data?.error_locations)
        ? parsed.yes_data.error_locations
        : Array.isArray(parsed?.error_locations)
          ? parsed.error_locations
          : [],
      reasons_by_location:
        parsed?.yes_data?.reasons_by_location ||
        parsed?.reasons_by_location ||
        {},
      notes_by_location:
        parsed?.yes_data?.notes_by_location || parsed?.notes_by_location || {},
    },
    history: Array.isArray(parsed?.history) ? parsed.history : [],
  };
};

const ErrorCell = ({ row, onSave }) => {
  const initialDetails = useMemo(
    () => parseErrorDetails(row.error_details),
    [row.error_details],
  );

  const [open, setOpen] = useState(false);
  const [errorExists, setErrorExists] = useState(row.error_exists || "");

  const [noErrorReason, setNoErrorReason] = useState(
    initialDetails.no_error_reason || "",
  );
  const [noAdditionalNotes, setNoAdditionalNotes] = useState(
    row.error_exists === "No" ? initialDetails.additional_notes || "" : "",
  );

  const [yesLocations, setYesLocations] = useState(
    initialDetails.yes_data?.error_locations || [],
  );
  const [yesReasonsByLocation, setYesReasonsByLocation] = useState(
    initialDetails.yes_data?.reasons_by_location || {},
  );
  const [yesNotesByLocation, setYesNotesByLocation] = useState(
    initialDetails.yes_data?.notes_by_location || {},
  );

  const [errors, setErrors] = useState({
    locations: false,
    noErrorReason: false,
    reasonsByLocation: {},
  });

  const userData = useUserData();
  const fullUserName =
    `${userData?.first_name || ""} ${userData?.last_name || ""}`.trim();

  const initialHistory = initialDetails.history || [];

  const handleOpen = (value) => {
    setErrorExists(value);
    setErrors({
      locations: false,
      noErrorReason: false,
      reasonsByLocation: {},
    });
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setErrors({
      locations: false,
      noErrorReason: false,
      reasonsByLocation: {},
    });
  };

  const handleLocationChange = (_, newLocations) => {
    const safeLocations = Array.isArray(newLocations) ? newLocations : [];
    setYesLocations(safeLocations);

    setYesReasonsByLocation((prev) => {
      const next = {};
      safeLocations.forEach((loc) => {
        next[loc] = Array.isArray(prev[loc]) ? prev[loc] : [];
      });
      return next;
    });

    setYesNotesByLocation((prev) => {
      const next = {};
      safeLocations.forEach((loc) => {
        next[loc] = prev[loc] || "";
      });
      return next;
    });

    setErrors((prev) => ({
      ...prev,
      locations: false,
      reasonsByLocation: Object.fromEntries(
        safeLocations.map((loc) => [loc, false]),
      ),
    }));
  };

  const handleReasonChange = (location, values) => {
    setYesReasonsByLocation((prev) => ({
      ...prev,
      [location]: values,
    }));

    setErrors((prev) => ({
      ...prev,
      reasonsByLocation: {
        ...prev.reasonsByLocation,
        [location]: false,
      },
    }));
  };

  const handleNoteChange = (location, value) => {
    setYesNotesByLocation((prev) => ({
      ...prev,
      [location]: value,
    }));
  };

  const handleSave = () => {
    const timestamp = new Date().toISOString();

    if (errorExists === "No") {
      const nextErrors = {
        locations: false,
        noErrorReason: !noErrorReason,
        reasonsByLocation: {},
      };

      setErrors(nextErrors);
      if (nextErrors.noErrorReason) return;

      const lastEntry = initialHistory[initialHistory.length - 1] || {};

      const noReasonChanged =
        lastEntry?.type !== "No" ||
        lastEntry?.no_error_reason !== noErrorReason ||
        (lastEntry?.additional_notes || "") !== noAdditionalNotes.trim();

      const basePayload = {
        current_status: "No",
        no_error_reason: noErrorReason,
        additional_notes: noAdditionalNotes.trim(),
        yes_data: {
          error_locations: yesLocations,
          reasons_by_location: yesReasonsByLocation,
          notes_by_location: yesNotesByLocation,
        },
      };

      if (!noReasonChanged) {
        onSave(row.patient_id, "No", {
          ...basePayload,
          history: initialHistory,
        });

        setOpen(false);
        return;
      }

      const newHistoryEntry = {
        type: "No",
        created_by: fullUserName || "Unknown User",
        created_at: timestamp,
        no_error_reason: noErrorReason,
        additional_notes: noAdditionalNotes.trim(),
      };

      onSave(row.patient_id, "No", {
        ...basePayload,
        history: [...initialHistory, newHistoryEntry],
      });

      setOpen(false);
      return;
    }

    const reasonsErrorMap = {};
    yesLocations.forEach((loc) => {
      reasonsErrorMap[loc] =
        !Array.isArray(yesReasonsByLocation[loc]) ||
        yesReasonsByLocation[loc].length === 0;
    });

    const hasReasonError = Object.values(reasonsErrorMap).some(Boolean);

    const nextErrors = {
      locations: yesLocations.length === 0,
      noErrorReason: false,
      reasonsByLocation: reasonsErrorMap,
    };

    setErrors(nextErrors);
    if (nextErrors.locations || hasReasonError) return;

    const lastEntry = initialHistory[initialHistory.length - 1] || {};

    const changedLocations = yesLocations.filter((loc) => {
      const prevReasons = lastEntry?.reasons_by_location?.[loc] || [];
      const newReasons = yesReasonsByLocation?.[loc] || [];

      const prevNote = lastEntry?.notes_by_location?.[loc] || "";
      const newNote = yesNotesByLocation?.[loc] || "";

      const reasonsChanged =
        JSON.stringify(prevReasons) !== JSON.stringify(newReasons);

      const noteChanged = prevNote !== newNote;

      const isNewLocation = !lastEntry?.error_locations?.includes(loc);

      return reasonsChanged || noteChanged || isNewLocation;
    });

    // update current yes_data even if nothing changed in history
    const basePayload = {
      current_status: "Yes",
      no_error_reason: noErrorReason,
      additional_notes: noAdditionalNotes.trim(),
      yes_data: {
        error_locations: yesLocations,
        reasons_by_location: yesReasonsByLocation,
        notes_by_location: yesNotesByLocation,
      },
    };

    // if nothing changed, don't append a new history entry
    if (!changedLocations.length) {
      onSave(row.patient_id, "Yes", {
        ...basePayload,
        history: initialHistory,
      });

      setOpen(false);
      return;
    }

    const newHistoryEntry = {
      type: "Yes",
      created_by: fullUserName || "Unknown User",
      created_at: timestamp,
      error_locations: changedLocations,
      reasons_by_location: Object.fromEntries(
        changedLocations.map((loc) => [loc, yesReasonsByLocation[loc] || []]),
      ),
      notes_by_location: Object.fromEntries(
        changedLocations.map((loc) => [loc, yesNotesByLocation[loc] || ""]),
      ),
    };

    onSave(row.patient_id, "Yes", {
      ...basePayload,
      history: [...initialHistory, newHistoryEntry],
    });

    setOpen(false);
  };

  const yesSelected = row.error_exists === "Yes";
  const noSelected = row.error_exists === "No";

  return (
    <>
      <Box display="flex" gap={1} py={1}>
        <Button
          size="small"
          variant={yesSelected ? "contained" : "outlined"}
          color="error"
          onClick={() => handleOpen("Yes")}
          sx={{ minWidth: 64 }}
        >
          Yes
        </Button>

        <Button
          size="small"
          variant={noSelected ? "contained" : "outlined"}
          color="success"
          onClick={() => handleOpen("No")}
          sx={{ minWidth: 64 }}
        >
          No
        </Button>
      </Box>

      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ pb: 1.5 }}>
          {errorExists === "Yes" ? "Add Error Details" : "Confirm No Errors"}
        </DialogTitle>

        <DialogContent dividers sx={{ pt: 3 }}>
          {errorExists === "No" && (
            <Box>
              <Typography
                fontSize={15}
                fontWeight={700}
                color="success.main"
                mb={2}
              >
                No errors
              </Typography>

              <TextField
                select
                fullWidth
                required
                size="small"
                label="Reason"
                value={noErrorReason}
                onChange={(e) => {
                  setNoErrorReason(e.target.value);
                  setErrors((prev) => ({ ...prev, noErrorReason: false }));
                }}
                error={errors.noErrorReason}
                helperText={errors.noErrorReason ? "Reason is required" : ""}
              >
                {NO_ERROR_OPTIONS.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                fullWidth
                size="small"
                multiline
                minRows={3}
                label="Additional Details (Optional)"
                value={noAdditionalNotes}
                onChange={(e) => setNoAdditionalNotes(e.target.value)}
                sx={{ mt: 2 }}
              />
            </Box>
          )}

          {errorExists === "Yes" && (
            <Box>
              <Typography
                fontSize={15}
                fontWeight={700}
                color="error.main"
                mb={2}
              >
                Error reported
              </Typography>

              <Typography fontSize={13} fontWeight={600} mb={1}>
                Error Location <span style={{ color: "#d32f2f" }}>*</span>
              </Typography>

              <ToggleButtonGroup
                value={yesLocations}
                onChange={handleLocationChange}
                size="small"
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 1,
                  "& .MuiToggleButton-root": {
                    borderRadius: "10px !important",
                    border: "1px solid #d0d5dd !important",
                    px: 2,
                    py: 1,
                    textTransform: "none",
                    fontWeight: 600,
                  },
                }}
              >
                {ERROR_LOCATIONS.map((item) => (
                  <ToggleButton key={item} value={item}>
                    {item}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>

              {errors.locations && (
                <Typography fontSize={12} color="error.main" mt={1}>
                  Please select at least one error location
                </Typography>
              )}

              {!!yesLocations.length && (
                <Box mt={2} display="flex" flexDirection="column" gap={2}>
                  {yesLocations.map((location) => (
                    <Box key={location}>
                      <Box mb={1} display="flex" alignItems="center" gap={1}>
                        <Chip
                          label={location}
                          size="small"
                          color="error"
                          variant="outlined"
                        />
                      </Box>

                      <FormControl
                        fullWidth
                        size="small"
                        error={!!errors.reasonsByLocation?.[location]}
                      >
                        <InputLabel>{`${location} Reasons`}</InputLabel>
                        <Select
                          multiple
                          value={yesReasonsByLocation[location] || []}
                          onChange={(e) =>
                            handleReasonChange(location, e.target.value)
                          }
                          input={
                            <OutlinedInput label={`${location} Reasons`} />
                          }
                          renderValue={(selected) => selected.join(", ")}
                        >
                          {(ERROR_REASON_OPTIONS[location] || []).map(
                            (option) => (
                              <MenuItem key={option} value={option}>
                                <Checkbox
                                  checked={
                                    (
                                      yesReasonsByLocation[location] || []
                                    ).indexOf(option) > -1
                                  }
                                />
                                <ListItemText primary={option} />
                              </MenuItem>
                            ),
                          )}
                        </Select>

                        {!!errors.reasonsByLocation?.[location] && (
                          <Typography fontSize={12} color="error.main" mt={0.5}>
                            At least one reason is required
                          </Typography>
                        )}
                      </FormControl>

                      <TextField
                        fullWidth
                        size="small"
                        multiline
                        minRows={3}
                        label={`${location} Note (Optional)`}
                        value={yesNotesByLocation[location] || ""}
                        onChange={(e) =>
                          handleNoteChange(location, e.target.value)
                        }
                        sx={{ mt: 1.5 }}
                      />
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            color={errorExists === "Yes" ? "error" : "success"}
            sx={{ minWidth: 96 }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ErrorCell;
